import logger from '@ayocore/platform/common/helpers/logger';
import type { AwaitableRpcClient } from '@ayocore/platform/server/rpc/types';
import type { ActivityThreadRepository } from '@ayocore/activity/server/repositories/ActivityThreadRepository';
import type { ReconcileSourcesResult } from '@ayocore/activity/server/activity/reconcile-from-sources';
import type { ActivityThreadQuickFilter } from '@ayocore/activity/common/types/activity-thread-quick-filter';
import type { ActivityFeedItem } from '@ayocore/activity/server/repositories/ActivityThreadRepository';
import { ControllerStore } from './ControllerStore';
import {
  DEFAULT_ACTIVITY_ARCHIVE_SUMMARY,
  INITIAL_THREADS_STATE,
  type ActivityThreadsState,
} from './types';

/**
 * ActivityThreadsController — состояние и действия списка тредов.
 *
 * Зона ответственности:
 * - загрузка списка тредов и счетчиков quick filters/архива;
 * - quick filter / archive view;
 * - pin/favorite/archive для треда;
 * - mark-as-read + busyThreadId;
 * - debounce reload по realtime.
 */
export class ActivityThreadsController extends ControllerStore<ActivityThreadsState> {
  private threadRepository: AwaitableRpcClient<ActivityThreadRepository>;

  private reloadTimer: ReturnType<typeof setTimeout> | null = null;

  private threadsRequestSeq = 0;

  constructor(threadRepository: AwaitableRpcClient<ActivityThreadRepository>) {
    super(INITIAL_THREADS_STATE);
    this.threadRepository = threadRepository;
  }

  /**
   * Обновляет зависимость репозитория, если rpc-хук вернул новый ref.
   */
  updateThreadRepository(nextRepository: AwaitableRpcClient<ActivityThreadRepository>): void {
    this.threadRepository = nextRepository;
  }

  /**
   * Реконсиляция треда из первоисточников (кнопка «Обновить» в шапке чата).
   * Возвращает счётчики восстановленного.
   */
  async reconcileThread(threadId: number): Promise<ReconcileSourcesResult> {
    return this.threadRepository.reconcileThread(threadId);
  }

  /**
   * Инвалидирует pending запросы и сбрасывает debounce таймер.
   */
  invalidatePending(): void {
    this.threadsRequestSeq += 1;
    if (this.reloadTimer) {
      clearTimeout(this.reloadTimer);
      this.reloadTimer = null;
    }
  }

  /**
   * Ищет тред в текущем snapshot по id.
   */
  findThreadById(threadId: number | null): ActivityFeedItem | null {
    if (!threadId || threadId <= 0) return null;
    return this.getSnapshot().items.find(item => item.id === threadId) ?? null;
  }

  /**
   * Загружает список тредов и счетчики.
   */
  async load(options?: {
    showLoader?: boolean;
    quickFilter?: ActivityThreadQuickFilter;
    isArchiveView?: boolean;
  }): Promise<void> {
    const showLoader = options?.showLoader ?? true;
    const snapshot = this.getSnapshot();
    const effectiveQuickFilter = options?.quickFilter ?? snapshot.quickFilter;
    const effectiveIsArchiveView = options?.isArchiveView ?? snapshot.isArchiveView;
    const requestId = ++this.threadsRequestSeq;

    try {
      logger.debug('[Activity:Threads] load started', {
        quickFilter: effectiveQuickFilter,
        isArchiveView: effectiveIsArchiveView,
        showLoader,
      });

      if (
        snapshot.quickFilter !== effectiveQuickFilter ||
        snapshot.isArchiveView !== effectiveIsArchiveView
      ) {
        this.setState({
          quickFilter: effectiveQuickFilter,
          isArchiveView: effectiveIsArchiveView,
        });
      }

      if (showLoader) {
        this.setState({
          loading: true,
          errorMessage: null,
        });
      }

      const [items, quickFilterCounters, archiveSummary] = await Promise.all([
        this.fetchFeed(effectiveQuickFilter, effectiveIsArchiveView),
        this.fetchQuickFilterCounters(effectiveIsArchiveView),
        this.fetchArchiveSummary(),
      ]);
      if (this.isThreadsRequestStale(requestId)) {
        logger.debug('[Activity:Threads] load stale, skipping', { requestId });
        return;
      }

      this.setState({
        items,
        quickFilterCounters,
        archiveSummary,
        errorMessage: null,
      });
      logger.debug('[Activity:Threads] load completed', {
        requestId,
        itemsCount: items.length,
      });
    } catch (error) {
      if (this.isThreadsRequestStale(requestId)) return;
      logger.error('[Activity:Threads] load failed', { error });
      this.setState({
        items: [],
        archiveSummary: { ...DEFAULT_ACTIVITY_ARCHIVE_SUMMARY },
        errorMessage:
          error instanceof Error ? error.message : 'Не удалось загрузить ленту активности.',
      });
    } finally {
      if (!this.isThreadsRequestStale(requestId)) {
        this.setState({ loading: false });
      }
    }
  }

  /**
   * Обновляет quick filter и перезагружает список.
   */
  async setQuickFilter(quickFilter: ActivityThreadQuickFilter): Promise<void> {
    if (this.getSnapshot().quickFilter === quickFilter) return;
    await this.load({
      showLoader: false,
      quickFilter,
    });
  }

  /**
   * Переключает режим архива и перезагружает список.
   */
  async setArchiveView(isArchiveView: boolean): Promise<void> {
    if (this.getSnapshot().isArchiveView === isArchiveView) return;
    await this.load({
      showLoader: false,
      isArchiveView,
    });
  }

  /**
   * Optimistic favorite-флаг треда.
   */
  async setThreadFavorite(threadId: number, isFavorite: boolean): Promise<void> {
    if (!threadId || threadId <= 0) return;

    try {
      this.setState({
        items: this.getSnapshot().items.map(item =>
          item.id === threadId ? { ...item, is_favorite: isFavorite } : item
        ),
      });

      await this.threadRepository.setThreadState(threadId, {
        is_favorite: isFavorite,
      });
      await this.load({ showLoader: false });
    } catch (error) {
      logger.error('[Activity:Threads] setThreadFavorite failed', {
        threadId,
        isFavorite,
        error,
      });
      await this.load({ showLoader: false });
    }
  }

  /**
   * Optimistic pin-флаг треда.
   */
  async setThreadPinned(threadId: number, isPinned: boolean): Promise<void> {
    if (!threadId || threadId <= 0) return;

    try {
      this.setState({
        items: this.getSnapshot().items.map(item =>
          item.id === threadId ? { ...item, is_pinned: isPinned } : item
        ),
      });

      await this.threadRepository.setThreadState(threadId, {
        is_pinned: isPinned,
      });
      await this.load({ showLoader: false });
    } catch (error) {
      logger.error('[Activity:Threads] setThreadPinned failed', {
        threadId,
        isPinned,
        error,
      });
      await this.load({ showLoader: false });
    }
  }

  /**
   * Optimistic archive-флаг треда.
   */
  async setThreadArchived(threadId: number, isArchived: boolean): Promise<void> {
    if (!threadId || threadId <= 0) return;

    try {
      this.setState({
        items: this.getSnapshot().items.map(item =>
          item.id === threadId
            ? {
                ...item,
                is_archived: isArchived,
                ...(isArchived ? { is_pinned: false, is_favorite: false } : {}),
              }
            : item
        ),
      });

      await this.threadRepository.setThreadState(threadId, {
        is_archived: isArchived,
        ...(isArchived ? { is_pinned: false, is_favorite: false } : {}),
      });
      await this.load({ showLoader: false });
    } catch (error) {
      logger.error('[Activity:Threads] setThreadArchived failed', {
        threadId,
        isArchived,
        error,
      });
      await this.load({ showLoader: false });
    }
  }

  /**
   * Debounce-загрузка тредов (realtime).
   */
  scheduleReload(debounceMs: number = 250): void {
    if (this.reloadTimer) {
      clearTimeout(this.reloadTimer);
    }
    this.reloadTimer = setTimeout(() => {
      void this.load({ showLoader: false });
    }, debounceMs);
  }

  /**
   * Помечает тред как прочитанный и обновляет local counters.
   */
  async markThreadRead(threadId: number): Promise<void> {
    if (!threadId || threadId <= 0) return;

    logger.debug('[Activity:Threads] markThreadRead started', { threadId });
    try {
      this.setState({
        busyThreadId: threadId,
      });

      await this.threadRepository.markThreadRead(threadId);

      this.setState({
        items: this.getSnapshot().items.map(item =>
          item.id === threadId
            ? {
                ...item,
                unread_count: 0,
                attention_unread_count: 0,
                last_read_event_id: item.last_event_id ?? item.last_read_event_id,
              }
            : item
        ),
      });
      logger.debug('[Activity:Threads] markThreadRead completed', { threadId });
    } catch (error) {
      logger.error('[Activity:Threads] markThreadRead failed', { threadId, error });
    } finally {
      this.setState({
        busyThreadId: null,
      });
    }
  }

  /**
   * Освобождает ресурсы контроллера тредов.
   */
  dispose(): void {
    if (this.reloadTimer) {
      clearTimeout(this.reloadTimer);
      this.reloadTimer = null;
    }
    super.dispose();
  }

  private isThreadsRequestStale(requestId: number): boolean {
    return requestId !== this.threadsRequestSeq;
  }

  private async fetchFeed(
    quickFilter: ActivityThreadQuickFilter,
    isArchiveView: boolean
  ): Promise<ActivityFeedItem[]> {
    const rows = await this.threadRepository.getMyFeed({
      limit: 200,
      include_archived: isArchiveView,
      only_archived: isArchiveView,
      quick_filter: quickFilter,
    });
    return Array.isArray(rows) ? rows : [];
  }

  private async fetchQuickFilterCounters(isArchiveView: boolean) {
    const counters = await this.threadRepository.getMyQuickFilterCounters({
      include_archived: isArchiveView,
      only_archived: isArchiveView,
    });
    return {
      favorites: Number(counters?.favorites ?? 0),
      favorites_unread_threads: Number(counters?.favorites_unread_threads ?? 0),
      favorites_overdue_threads: Number(counters?.favorites_overdue_threads ?? 0),
      dm: Number(counters?.dm ?? 0),
      projects: Number(counters?.projects ?? 0),
      new_leads: Number(counters?.new_leads ?? 0),
      unread: Number(counters?.unread ?? 0),
      attention: Number(counters?.attention ?? 0),
      my_overdue: Number(counters?.my_overdue ?? 0),
      overdue: Number(counters?.overdue ?? 0),
    };
  }

  private async fetchArchiveSummary() {
    const summary = await this.threadRepository.getMyArchiveSummary();
    if (!summary || typeof summary !== 'object') {
      return { ...DEFAULT_ACTIVITY_ARCHIVE_SUMMARY };
    }
    return {
      archived_threads_count: Number(summary.archived_threads_count ?? 0),
      unread_count: Number(summary.unread_count ?? 0),
      attention_unread_count: Number(summary.attention_unread_count ?? 0),
      my_overdue_count: Number(summary.my_overdue_count ?? 0),
      overdue_count: Number(summary.overdue_count ?? 0),
    };
  }
}
